import type { AnnulOperation } from '@festina/configuration-general-types/configuration'
import { useMemo } from 'react'

import { useAnnulOperationDescriptors } from '../../uiModels/annulOperations/useAnnulOperationDescriptors'
import type { OperationWithSourceInfo } from '../../uiModels/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { LocationAwareOperationLink } from '../configurationOperationLink/LocationAwareOperationLink'
import type { OperationsListVariant } from './types'

type Props = {
  variant: OperationsListVariant
  block: string
}

export const AnnulOperationsList = ({ variant, block }: Props) => {
  const { allAnnulOperationsByKey, workspaceKey, branchKey } =
    useOperationsContext()
  const descriptors = useAnnulOperationDescriptors()

  const draftAnnulOperations = useMemo(
    () =>
      Array.from(allAnnulOperationsByKey.values()).filter(
        (
          annulOperation,
        ): annulOperation is OperationWithSourceInfo<AnnulOperation> =>
          annulOperation.isDraft && annulOperation.block === block,
      ),
    [allAnnulOperationsByKey, block],
  )

  if (variant === 'errors') {
    // Currently annul operations do not have any backend validations associated to them.
    return null
  }

  return draftAnnulOperations.map((annulOperation) => {
    const { internalOperation, block: annulBlock, layer } = annulOperation
    const { operationKey } = internalOperation
    if (!operationKey) {
      return null
    }

    const descriptor = internalOperation.createOperationClassName
      ? descriptors[internalOperation.createOperationClassName]
      : undefined
    const iconType = descriptor?.configurationItemVariant ?? 'annulOperation'

    return (
      <LocationAwareOperationLink
        key={operationKey}
        type="annulOperation"
        iconType={iconType}
        itemId={operationKey}
        itemLabel={internalOperation.name}
        block={annulBlock}
        layer={layer}
        workspaceKey={workspaceKey}
        branchKey={branchKey}
        operation={internalOperation}
        operationKey={operationKey}
        isDraft
      />
    )
  })
}
